import { log } from 'log';
import { prop } from 'ramda';
import { useCallback } from 'react';
import { useSocketSelector } from 'js/store/hooks';

import { removeStackEntry, addStackEntry } from '../../helpers';
import * as helpers from './helpers';
import * as constants from './constants';
import { IServerSendResponse, TServerSendRequest } from '../../types';

export const useGenericSend = () => {
    const connection = useSocketSelector(prop('connection'));

    return useCallback(
        <RequestData = object, ResponseData = object>(
            request: TServerSendRequest<RequestData>
        ): Promise<IServerSendResponse<ResponseData>> => {
            const promise = new Promise<IServerSendResponse<ResponseData>>((resolve, reject) => {
                const timestamp = Date.now();

                log('oapiRequest', request);

                if (!connection?.socket?.connected) {
                    reject({
                        reason: 'No connection',
                        request
                    });

                    return;
                }

                const updatedRequest = helpers.updateCorrelationId(helpers.addRelativeUri(request));

                if (request.ID === constants.BATCH_REQUEST_ID && request.requests) {
                    const promises: Promise<void>[] = [];

                    for (const subRequest of request.requests.map(helpers.updateCorrelationId)) {
                        let subResolve: any;
                        let subReject: any;

                        promises.push(
                            new Promise((localResolve, localReject) => {
                                subResolve = localResolve;
                                subReject = localReject;
                            })
                        );

                        const stackEntry = {
                            request: subRequest,
                            resolve: subResolve,
                            reject: subReject,
                            timestamp,
                            timer: setTimeout(() => {
                                removeStackEntry(stackEntry);

                                reject({
                                    reason: 'Request timed out',
                                    request: subRequest
                                });
                            }, 30000)
                        };
                    }

                    const stackEntry = {
                        request: updatedRequest,
                        resolve,
                        reject,
                        timestamp,
                        timer: setTimeout(() => {
                            removeStackEntry(stackEntry);

                            reject({
                                reason: 'Request timed out',
                                request: updatedRequest
                            });
                        }, constants.REQUEST_TIMEOUT)
                    };

                    addStackEntry(stackEntry);
                    connection.send(JSON.stringify(updatedRequest));

                    return Promise.all(promises);
                }

                if (request.responses) {
                    const stackEntry = {
                        request: updatedRequest,
                        resolve,
                        reject,
                        timestamp,
                        timer: setTimeout(() => {
                            removeStackEntry(stackEntry);

                            reject({
                                reason: 'Request timed out',
                                request: updatedRequest
                            });
                        }, constants.REQUEST_TIMEOUT)
                    };

                    addStackEntry(stackEntry);
                }

                connection.send(JSON.stringify(updatedRequest));
            });

            promise.catch((error) => {
                log('oapiError', error);

                throw error;
            });

            return promise;
        },
        [connection]
    );
};
